import { type ButtonAction, routeParamNames } from '@luminovo/apps-types';

/** A button action that navigates, the only kind a target is filled for. */
export type NavigateButtonAction = Extract<ButtonAction, { type: 'navigate' }>;

export type FillNavigateTargetResult = { path: string } | { missing: string[] };

/**
 * The product path a `navigate` button goes to: the destination route with each
 * `:param` filled from the slot, and any other mapped param in the query string.
 *
 * Every mapped value must be a non-empty string at click time; the names that are
 * not come back as `missing`, so the host can withhold the button instead of
 * sending the user to a half-filled URL. Each value is `encodeURIComponent`ed, so
 * a slot value can never add a path segment.
 */
export function fillNavigateTarget(
    route: string,
    action: NavigateButtonAction,
    slotParams: Readonly<Record<string, unknown>>,
): FillNavigateTargetResult {
    const values = new Map<string, string>();
    const missing: string[] = [];
    for (const [name, source] of Object.entries(action.params)) {
        const value = slotParams[source.slot];
        if (typeof value !== 'string' || value === '') {
            missing.push(source.slot);
            continue;
        }
        values.set(name, value);
    }
    if (missing.length > 0) return { missing };

    const routeParams = new Set(routeParamNames(route));
    for (const name of routeParams) {
        // Upload refuses an unmapped route param; a stored manifest is not re-parsed.
        if (!values.has(name)) missing.push(name);
    }
    if (missing.length > 0) return { missing };

    // A wildcard route's landing page is its base, so drop the trailing `/*`.
    const pathname = (route.endsWith('/*') ? route.slice(0, -2) : route)
        .split('/')
        .map((segment) => (segment.startsWith(':') ? encodeURIComponent(values.get(segment.slice(1))!) : segment))
        .join('/');
    const query = new URLSearchParams();
    for (const [name, value] of values) {
        if (!routeParams.has(name)) query.set(name, value);
    }
    const search = query.toString();
    return { path: search === '' ? pathname : `${pathname}?${search}` };
}
